import { useForm } from '@tanstack/react-form'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { create } from '@bufbuild/protobuf'
import { toast } from 'sonner'

import { CreateTaxRateRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { useCreateTaxRate } from '@/stores/organization.store'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TaxTypeCombobox } from '@/components/settings/tax-type-combobox'

interface TaxRateCreateSheetProps {
  organizationId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  /** 'inline' renders the form body directly instead of inside a Sheet overlay — used by the onboarding wizard. */
  variant?: 'sheet' | 'inline'
}

/**
 * TaxRateCreateSheet - Sheet component for creating new tax rates
 *
 * Features:
 * - Create new tax rate with tax_type, name, and percentage
 * - Uses TanStack Form for form state management
 * - Leverages useCreateTaxRate hook for optimized cache updates
 * - Displays loading states during mutation
 * - Shows toast notifications on success/error
 * - Resets form on close
 *
 * @param organizationId - The organization ID to create the tax rate for
 * @param open - Control sheet visibility
 * @param onOpenChange - Handle open/close state changes
 * @param onSuccess - Optional callback when tax rate is created successfully
 * @param variant - 'sheet' (default) or 'inline' for the onboarding wizard
 */
export function TaxRateCreateSheet({
  organizationId,
  open,
  onOpenChange,
  onSuccess,
  variant = 'sheet',
}: TaxRateCreateSheetProps) {
  const { mutation } = useCreateTaxRate()

  // Setup TanStack Form
  const form = useForm({
    defaultValues: {
      taxType: '',
      name: '',
      percentage: 0,
    },
    onSubmit: async ({ value }) => {
      try {
        const taxType = value.taxType.trim()
        if (!taxType) {
          toast.error('Please enter a tax type')
          return
        }
        if (!value.name.trim()) {
          toast.error('Please enter a tax name')
          return
        }
        if (value.percentage < 0 || value.percentage > 100) {
          toast.error('Percentage must be between 0 and 100')
          return
        }

        // Build CreateTaxRateRequest
        const request = create(CreateTaxRateRequestSchema, {
          organizationId,
          taxType,
          name: value.name.trim(),
          percentage: value.percentage,
        })

        await mutation.mutateAsync(request)

        toast.success('Tax rate created successfully')
        onOpenChange(false)
        form.reset()

        // Call optional success callback
        if (onSuccess) {
          onSuccess()
        }
      } catch (error) {
        console.error('Failed to create tax rate:', error)
        reportError(error, { feature: 'tax_rate_create', kind: 'mutation' })
        toast.error('Failed to create tax rate', {
          description: getErrorDescription(error),
        })
      }
    },
  })

  const formBody = (
    <form
      id="tax-rate-form"
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="grid auto-rows-min gap-6"
    >
      <form.Field name="name">
        {(field) => (
          <div className="grid gap-3">
            <Label htmlFor="tax-name">
              Tax Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="tax-name"
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder="e.g., Travis County or Texas Sales Tax"
              disabled={mutation.isPending}
            />
            <p className="text-sm text-muted-foreground">
              Enter either a county name or the name of the sales tax.
            </p>
          </div>
        )}
      </form.Field>

      <form.Field name="taxType">
        {(field) => (
          <div className="grid gap-3">
            <Label htmlFor="tax-type">
              Tax Type <span className="text-destructive">*</span>
            </Label>
            <TaxTypeCombobox
              id="tax-type"
              value={field.state.value}
              onChange={field.handleChange}
              onBlur={field.handleBlur}
              disabled={mutation.isPending}
            />
            <p className="text-sm text-muted-foreground">
              Type your own value or pick from the suggestions.
            </p>
          </div>
        )}
      </form.Field>

      <form.Field name="percentage">
        {(field) => (
          <div className="grid gap-3">
            <Label htmlFor="tax-percentage">
              Percentage (%) <span className="text-destructive">*</span>
            </Label>
            <Input
              id="tax-percentage"
              type="number"
              min="0"
              max="100"
              step="0.001"
              value={field.state.value}
              onChange={(e) => field.handleChange(Number(e.target.value) || 0)}
              onBlur={(e) => {
                const value = Number(e.target.value) || 0
                // Enforce minimum of 0 and maximum of 100
                const clampedValue = Math.max(0, Math.min(100, value))
                field.handleChange(clampedValue)
                field.handleBlur()
              }}
              disabled={mutation.isPending}
              placeholder="0.000"
            />
            <p className="text-sm text-muted-foreground">
              Enter the tax percentage (e.g., 8.255 for 8.255%)
            </p>
          </div>
        )}
      </form.Field>
    </form>
  )

  const submitButton = (
    <form.Subscribe selector={(s) => [s.values, s.isSubmitting] as const}>
      {([values, isSubmitting]) => {
        const disabled =
          mutation.isPending ||
          isSubmitting ||
          !values.name.trim() ||
          !values.taxType.trim() ||
          values.percentage < 0 ||
          values.percentage > 100
        return (
          <Button type="submit" form="tax-rate-form" disabled={disabled}>
            {mutation.isPending ? 'Creating...' : 'Create Tax Rate'}
          </Button>
        )
      }}
    </form.Subscribe>
  )

  if (variant === 'inline') {
    if (!open) return null

    return (
      <div className="space-y-6 rounded-lg border bg-card p-6">
        <div className="space-y-1.5">
          <h3 className="text-base font-semibold text-foreground">Create New Tax Rate</h3>
          <p className="text-sm text-muted-foreground">
            Add a new sales tax rate for your organization.
          </p>
        </div>
        {formBody}
        <div className="flex flex-col-reverse gap-2 border-t border-border/50 pt-4 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            disabled={mutation.isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          {submitButton}
        </div>
      </div>
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Create New Tax Rate</SheetTitle>
          <SheetDescription>
            Add a new sales tax rate for your organization.
          </SheetDescription>
        </SheetHeader>
        <SheetBody>{formBody}</SheetBody>
        <SheetFooter>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </SheetClose>
          {submitButton}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
